import type { SpellList } from "../../types";

export const spellLists: SpellList[] = [
  {
    id: "destruction",
    name: "Spells of Destruction",
    spells: [
      {
        id: "blast",
        name: "Blast",
        listId: "destruction",
        duration: "1 minute",
        effect:
          "Throw a LARP-safe spell packet. On hit, the target takes 1 damage (ignores armour damage reduction).",
        scaling: "multicast",
        scalingDescription:
          "Each additional point allows Blast to be cast again within 1 minute for no extra MP cost. Max level 8.",
        full_description: [
          {
            type: "paragraph",
            text: "Throw a LARP-safe spell packet at the target. On a hit, they take 1 damage. For every extra point invested into this spell, Blast can be cast again within the next minute for no extra MP cost. For example, 6 points in Blast allows you to throw 6 spell packets for 1 MP. Blast ignores the damage reduction of armour, always doing 1 damage.",
          },
        ],
      },
    ],
  },
  {
    id: "warMagic",
    name: "Spells of War Magic",
    notes: [
      "Targets can be under the effects of multiple spells at once. For example, a creature affected by Duel can also be targeted with Shrink, to force them to fight someone specific whilst on their knees.",
      "For every extra point invested into any of these spells, they may be cast an additional time within the next minute for no extra cost. For example, a Warlock with level 3 Knockback can use it 3 times in quick succession for only 1 MP.",
    ],
    spells: [
      {
        id: "blind",
        name: "Blind",
        listId: "warMagic",
        duration: "5 seconds",
        effect: "The target cannot look at the caster.",
        scaling: "multicast",
        scalingDescription:
          "Each additional point allows Blind to be cast again within 1 minute for no extra MP cost.",
        full_description: [
          {
            type: "paragraph",
            text: "The target cannot look at the caster.",
          },
        ],
      },
      {
        id: "fear",
        name: "Fear",
        listId: "warMagic",
        duration: "5 seconds",
        effect: "The target must flee from the caster.",
        scaling: "multicast",
        scalingDescription:
          "Each additional point allows Fear to be cast again within 1 minute for no extra MP cost.",
        full_description: [
          {
            type: "paragraph",
            text: "The target must flee from the caster.",
          },
        ],
      },
      {
        id: "restrict",
        name: "Restrict",
        listId: "warMagic",
        duration: "5 seconds",
        effect: "The target's feet are locked in place.",
        scaling: "multicast",
        scalingDescription:
          "Each additional point allows Restrict to be cast again within 1 minute for no extra MP cost.",
        full_description: [
          {
            type: "paragraph",
            text: "The target's feet are locked in place.",
          },
        ],
      },
      {
        id: "berserk",
        name: "Berserk",
        listId: "warMagic",
        duration: "30 seconds",
        effect:
          "The target must attack the nearest creature. Berserk cannot to be used to make players target non-combatants or Familiars.",
        scaling: "multicast",
        scalingDescription:
          "Each additional point allows Berserk to be cast again within 1 minute for no extra MP cost.",
        full_description: [
          {
            type: "paragraph",
            text: "The target must attack the nearest creature. Berserk cannot to be used to make players target non-combatants or Familiars.",
          },
        ],
      },
      {
        id: "duel",
        name: "Duel",
        listId: "warMagic",
        duration: "30 seconds",
        effect: "The target may only willingly engage the caster in combat.",
        scaling: "multicast",
        scalingDescription:
          "Each additional point allows Duel to be cast again within 1 minute for no extra MP cost.",
        full_description: [
          {
            type: "paragraph",
            text: "The target may only willingly engage the caster in combat.",
          },
        ],
      },
      {
        id: "shrink",
        name: "Shrink",
        listId: "warMagic",
        duration: "30 seconds",
        effect:
          "The target crouches or drops to their knees (if safe to do so).",
        scaling: "multicast",
        scalingDescription:
          "Each additional point allows Shrink to be cast again within 1 minute for no extra MP cost.",
        deprecated: true,
        full_description: [
          {
            type: "paragraph",
            text: "The target crouches or drops to their knees (if safe to do so).",
          },
        ],
      },
      {
        id: "knockback",
        name: "Knockback",
        listId: "warMagic",
        duration: "Instant",
        effect: "The target must step back 1 metre (if safe to do so)",
        scaling: "multicast",
        scalingDescription:
          "Each additional point allows Knockback to be cast again within 1 minute for no extra MP cost.",
        full_description: [
          {
            type: "paragraph",
            text: "The target must step back 1 metre (if safe to do so)",
          },
        ],
      },
    ],
  },
  {
    id: "protection",
    name: "Spells of Protection",
    spells: [
      {
        id: "heal",
        name: "Heal",
        listId: "protection",
        duration: "Instant",
        effect:
          "Target immediately regains 1 HP (up to max). Instantly stops Bleeding Out.",
        scaling: "multicast",
        scalingDescription:
          "Each additional point allows Heal to be cast again within 1 minute for no extra MP cost.",
        full_description: [
          {
            type: "paragraph",
            text: "The target immediately regains 1 HP, up to their maximum. For every extra point invested into this spell, Heal can be cast again within the next minute for no extra MP cost. Casting Heal on someone instantly stops them Bleeding Out.",
          },
        ],
      },
      {
        id: "mage-armour",
        name: "Mage Armour",
        listId: "protection",
        duration: "1 day",
        effect:
          "All damage from the first hit taken by the target after casting is ignored. Can only be cast once per Short Rest.",
        scaling: "stacking",
        scalingDescription:
          "Each additional point causes one more subsequent hit to be ignored (counts even 0-damage hits).",
        full_description: [
          {
            type: "paragraph",
            text: "All damage from the first hit taken by the target after the spell is cast is ignored. For every extra point invested into this spell, an additional subsequent hit is ignored (Mage Armour counts any hits taken, even if they would have done 0 damage).",
          },
          {
            type: "paragraph",
            text: "Mage Armour can only be cast once per Short Rest.",
          },
        ],
      },
      {
        id: "resist",
        name: "Resist",
        listId: "protection",
        duration: "Instant",
        effect:
          "The target ignores one spell just cast on them. Costs 2 MP to cast.",
        scaling: "multicast",
        scalingDescription:
          "Each additional point allows Resist to be cast again within 1 minute for no extra MP cost.",
        full_description: [
          {
            type: "paragraph",
            text: "The target ignores one spell that has just been cast on them. The effort required to cancel out another creature's magic means that this spell costs 2 MP to cast. For every extra point invested into this spell, Resist can be cast again within the next minute for no extra MP cost.",
          },
        ],
      },
    ],
  },
  {
    id: "sorcery",
    name: "Spells of Sorcery",
    spells: [
      {
        id: "charm",
        name: "Charm",
        listId: "sorcery",
        duration: "5 minutes",
        effect:
          "Target regards you as a close and trusted friend. After duration, can spend 1 additional MP to erase target's memory of the Charm's duration.",
        scaling: "binary",
        full_description: [
          {
            type: "paragraph",
            text: "The target regards you as a close and trusted friend for the duration. Once the spell ends, you may spend 1 additional MP to erase the target's memory of the Charm's duration.",
          },
        ],
      },
      {
        id: "divination",
        name: "Divination",
        listId: "sorcery",
        duration: "1 minute",
        effect:
          "After 1 minute of ritual with appropriate tools (cards, bones, crystal ball), ask a GM one yes/no/unclear question.",
        scaling: "binary",
        full_description: [
          {
            type: "paragraph",
            text: "After spending at least 1 minute of roleplay enacting a ritual with appropriate tools, such as cards, bones, or a crystal ball, you may ask a GM one question that will be answered honestly with either 'yes', 'no', or 'unclear'.",
          },
        ],
      },
      {
        id: "ghost-walk",
        name: "Ghost Walk",
        listId: "sorcery",
        duration: "1 hour",
        effect:
          "Your spirit leaves your body and you enter the Ghost World. Leave a marker where your body lies and wear a blue headband. Cannot take material possessions. Return by contacting your body marker. Spend 1MP per additional creature while casting the spell to extend the effect.",
        scaling: "binary",
        full_description: [
          {
            type: "paragraph",
            text: "Your spirit leaves your body and you enter the Ghost World. Leave a clearly labelled marker where your physical body lies, and put on a blue headband to represent your transition to the spirit realm. While in the Ghost World, you cannot take any material possessions with you, or interact with anyone in the physical world (with some exceptions). To return to your true body, you must make contact with the marker you left behind, and you may instantly return to your physical form.",
          },
          {
            type: "paragraph",
            text: "You may bring additional creatures with you into the Ghost World by spending 1 additional MP per creature when casting the spell.",
          },
        ],
      },
      {
        id: "inspiration",
        name: "Inspiration",
        listId: "sorcery",
        duration: "1 minute",
        effect:
          "After 1 minute of inspiring performance or speech, everyone who could hear you gains 2 maximum HP until their next Short Rest (stackable, cannot exceed 8 HP).",
        scaling: "binary",
        full_description: [
          {
            type: "paragraph",
            text: "After spending at least 1 minute roleplaying an inspiring performance or speech, everyone who could hear you for the duration gains 2 maximum HP that lasts until their next Short Rest. This benefit can be stacked, but each Inspiration must come from a different person, and cannot increase the HP limit beyond 8.",
          },
        ],
      },
      {
        id: "invisibility",
        name: "Invisibility",
        listId: "sorcery",
        duration: "1 minute, extend 1MP per minute",
        effect:
          'A creature or item you touch becomes invisible (arms crossed = invisible). Ends if the creature casts spells or attacks. Special "Stun" attack available while Invisible.',
        scaling: "binary",
        full_description: [
          {
            type: "paragraph",
            text: "For 1 minute, a creature or item you touch becomes invisible. If the creature casts any spells or attacks anyone, the invisibility immediately ends.",
          },
          {
            type: "paragraph",
            text: "You may extend the duration of Invisibility by spending 1 MP per minute - and can do so while still invisible.",
          },
          {
            type: "paragraph",
            text: 'While invisible, you may use a special Stun attack to briefly incapacitate your enemies. Clearly call "Stun" and strike the target. See the "Stun" effect below. This counts as an attack and ends your invisibility.',
          },
          {
            type: "paragraph",
            text: "Invisibility is represented by crossing your arms in front of you.",
          },
          {
            type: "table",
            headers: ["Stun"],
            rows: [
              [
                "When a creature is Stunned, they are unresponsive for 5 seconds.",
              ],
              [
                "If you are Stunned, you are unaware of your surroundings, cannot speak or use spells or abilities, and will lose all memory of the time you are Stunned.",
              ],
              ["Taking damage of any kind immediately ends a Stun."],
            ],
          },
        ],
      },
      {
        id: "speak-with-dead",
        name: "Speak with Dead",
        listId: "sorcery",
        duration: "5 minutes",
        effect:
          "Bestow limited sentience into a corpse. It can speak but not move. Not compelled to be honest.",
        scaling: "binary",
        full_description: [
          {
            type: "paragraph",
            text: "For 5 minutes, you bestow limited sentience into the corpse of a dead creature. During this time it cannot physically move, except to speak. Dead creatures are not compelled to tell the truth, or share information they may not want to!",
          },
        ],
      },
    ],
  },
];
